import { SyncIcon, UploadIcon } from "@primer/octicons-react";
import { Box, Button as PrimerButton } from "@primer/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hocSinhApi } from "../../api/hocSinhApi";
import { ComboboxHe } from "../../components/combobox-he";
import ComboboxKhoi from "../../components/combobox-khoi";
import { ComboboxTruong } from "../../components/combobox-truong";
import { AnimationConfirm } from "../../components/confirm";
import DataGrid from "../../components/data-grid";
import { IDataGridColumn } from "../../components/data-grid/type/IDataGridProps";
import HocSinhShortInfoNew from "../../components/hoc-sinh/HocSinhShortInfoNew";
import SelectBoxMultipleHinhThucMienGiam from "../../components/selectbox-multiple-hinhthucmiengiam";
import Button from "../../components/ui/button";
import Text from "../../components/ui/text";
import TextWithEllipse from "../../components/ui/text-with-ellipse";
import { localStorageHelper } from "../../helpers/localStorage";
import { PageBaseStatus } from "../../models/pageBaseStatus";
import { ganMienGiamFilterStudentModel } from "../../models/request/gan-mien-giam/ganMienGiamFilterStudent";
import { ganMienGiamHocSinhRequest } from "../../models/request/gan-mien-giam/ganMienGiamHocSinhRequest";
import {
  approveGanMienGiamCancel,
  approveGanMienGiamSubmit,
  changeGanMienGiamFilterStudent,
  changeHe,
  changeKhoi,
  changeTruong,
  closeGanMienGiamFilterStudentModal,
  closeGanMienGiamImportModal,
  ganMienGiamHocSinhStart,
  loadGanMienGiamHinhThucMienGiamStart,
  loadGanMienGiamHocSinhStart,
  loadGanMienGiamStart,
  showGanMienGiamImportModal,
} from "../../state/actions/ganMienGiamActions";
import { RootState } from "../../state/reducers";
import { GanMienGiamFilterStudentModal } from "./filter-student-modal";
import styles from "./GanMienGiamPage.module.css";
import { GanMienGiamImportModal } from "./import-modal/GanMienGiamImportModal";

const GanMienGiamNewPage = () => {
  const state = useSelector((x: RootState) => x.ganMienGiam);
  const dispatch = useDispatch();
  const { nam_hoc, dm_coso_id } = useSelector((x: RootState) => x.common);
  const categorySource = useSelector((x: RootState) => x.categorySource);
  const [filterMode, setFilterMode] = useState<
    "all" | "has_discount" | "no_discount"
  >("all");
  const [dmTrangThaiHocSinhs, setDmTrangThaiHocSinhs] = useState<any[]>([]);

  useEffect(() => {
    hocSinhApi
      .selectTrangThai()
      .then((res: any) => {
        if (res?.is_success && Array.isArray(res.data)) {
          setDmTrangThaiHocSinhs(res.data);
        }
      })
      .catch(() => {});
  }, []);

  const {
    is_show_filter_student_modal,
    filterStudent,
    sf_dexuatmiengiams,
    status,
    ts_hocsinhs,
    approve_request,
    is_show_approve_confirm,
    is_show_import_modal,
    dm_he_id,
    dm_khoi_id,
    dm_truong_id,
    nhom_mien_giam_id,
    hinh_thuc_mien_giam_ids,
  } = state;

  const sf_hinhthucmiengiams = useMemo(() => {
    return [...state.sf_hinhthucmiengiams]
      .filter((x) => !dm_coso_id || x.dm_coso_id === dm_coso_id)
      .sort(
        (a, b) =>
          (a.thutu_nhommiengiam ?? 999) - (b.thutu_nhommiengiam ?? 999) ||
          (a.sf_hinhthucmiengiam_group_id ?? 0) -
            (b.sf_hinhthucmiengiam_group_id ?? 0) ||
          a.id - b.id
      );
  }, [state.sf_hinhthucmiengiams, dm_coso_id]);

  useEffect(() => {
    if (
      status === PageBaseStatus.is_need_reload ||
      status === PageBaseStatus.is_not_initial
    ) {
      dispatch(loadGanMienGiamHinhThucMienGiamStart(nam_hoc));
    }
  }, [status, nam_hoc, dispatch]);

  useEffect(() => {
    if (nam_hoc !== "") {
      dispatch(loadGanMienGiamHinhThucMienGiamStart(nam_hoc));
    }
  }, [nam_hoc, dispatch]);

  useEffect(() => {
    if (!nam_hoc) return;
    dispatch(
      loadGanMienGiamHocSinhStart({
        dm_he_id: dm_he_id || 0,
        dm_khoi_id: dm_khoi_id || 0,
        dm_truong_id: dm_truong_id || 0,
        nam_hoc: nam_hoc,
      })
    );
    dispatch(
      loadGanMienGiamStart({
        dm_he_id: dm_he_id || 0,
        dm_khoi_id: dm_khoi_id || 0,
        dm_truong_id: dm_truong_id || 0,
        nam_hoc: nam_hoc,
      })
    );
    localStorageHelper.setNamHocTruongKhoiFilter({
      dm_he_id: dm_he_id !== undefined ? dm_he_id : 0,
      dm_khoi_id: dm_khoi_id !== undefined ? dm_khoi_id : 0,
      dm_truong_id: dm_truong_id !== undefined ? dm_truong_id : 0,
      nam_hoc: nam_hoc,
    });
  }, [dm_he_id, dm_khoi_id, dm_truong_id, nam_hoc, dm_coso_id, dispatch]);

  useEffect(() => {
    if (status === PageBaseStatus.is_need_reload && nam_hoc) {
      dispatch(
        loadGanMienGiamStart({
          dm_he_id: dm_he_id || 0,
          dm_khoi_id: dm_khoi_id || 0,
          dm_truong_id: dm_truong_id || 0,
          nam_hoc: nam_hoc,
        })
      );
    }
  }, [status, dm_he_id, dm_khoi_id, dm_truong_id, nam_hoc, dispatch]);

  const handleGanMienGiam = useCallback(
    (
      ts_hocsinh_id: number,
      current_nam_hoc: string,
      selected_hinhthuc_ids: number[]
    ) => {
      const data_send: ganMienGiamHocSinhRequest = {
        ts_hocsinh_id: ts_hocsinh_id,
        nam_hoc: current_nam_hoc,
        sf_hinhthucmiengiam_ids: selected_hinhthuc_ids,
      };
      dispatch(ganMienGiamHocSinhStart(data_send));
    },
    [dispatch]
  );

  const allStudentsProcessed = useMemo(() => {
    const khoiMap = new Map<number, string>(
      (categorySource?.dm_khois || []).map((k: any) => [k.id, k.ten_khoi])
    );
    const heMap = new Map<number, string>(
      (categorySource?.dm_hes || []).map((h: any) => [h.id, h.ten_he])
    );
    const trangThaiMap = new Map<number, string>(
      (dmTrangThaiHocSinhs || []).map((tt: any) => [tt.id, tt.trang_thai])
    );
    const truongCoSoSet = new Set<number>(
      (categorySource?.dm_truongs || [])
        .filter((t: any) => !dm_coso_id || t.dm_coso_id === dm_coso_id)
        .map((t: any) => t.id)
    );

    const dexuatByHocSinh = new Map<number, any[]>();
    sf_dexuatmiengiams.forEach((dx) => {
      const list = dexuatByHocSinh.get(dx.ts_hocsinh_id) || [];
      list.push(dx);
      dexuatByHocSinh.set(dx.ts_hocsinh_id, list);
    });

    const hinhthucMap = new Map<number, any>(
      sf_hinhthucmiengiams.map((ht) => [ht.id, ht])
    );

    const filteredStudents = ts_hocsinhs.filter((x: any) => {
      if (dm_coso_id && x.dm_coso_id && x.dm_coso_id !== dm_coso_id) {
        return false;
      }
      if (
        dm_coso_id &&
        !x.dm_coso_id &&
        x.dm_truong_id &&
        truongCoSoSet.size > 0 &&
        !truongCoSoSet.has(x.dm_truong_id)
      ) {
        return false;
      }
      return true;
    });

    const result = filteredStudents.map((x: any) => {
      const sf_dexuatmiengiam_hocsinhs = dexuatByHocSinh.get(x.id) || [];
      const resolvedTenKhoi =
        x.ten_khoi || khoiMap.get(x.dm_khoi_id) || "";
      const resolvedTenHe = x.ten_he || heMap.get(x.dm_he_id) || "";

      const appliedPolicies: {
        id: number;
        ma_hinh_thuc: string;
        hinh_thuc_mien_giam: string;
        ten_nhom_mien_giam: string;
        thutu_nhommiengiam: number;
        so_lan_ap_dung: number;
      }[] = [];

      const selectedIds: number[] = [];

      for (let i = 0; i < sf_dexuatmiengiam_hocsinhs.length; i++) {
        const dx = sf_dexuatmiengiam_hocsinhs[i];
        const sf_hinhthucmiengiam_id = dx.sf_hinhthucmiengiam_id;
        const sf_hinhthucmiengiam = hinhthucMap.get(sf_hinhthucmiengiam_id);
        if (sf_hinhthucmiengiam) {
          appliedPolicies.push({
            id: sf_hinhthucmiengiam.id,
            ma_hinh_thuc: sf_hinhthucmiengiam.ma_hinh_thuc,
            hinh_thuc_mien_giam: sf_hinhthucmiengiam.hinh_thuc_mien_giam,
            ten_nhom_mien_giam: sf_hinhthucmiengiam.ten_nhom_mien_giam || "",
            thutu_nhommiengiam: sf_hinhthucmiengiam.thutu_nhommiengiam ?? 999,
            so_lan_ap_dung: dx.so_lan_ap_dung,
          });
          selectedIds.push(sf_hinhthucmiengiam_id);
        }
      }

      appliedPolicies.sort(
        (a, b) => a.thutu_nhommiengiam - b.thutu_nhommiengiam || a.id - b.id
      );

      const summary = appliedPolicies
        .map((p) => `* [${p.ma_hinh_thuc}] ${p.hinh_thuc_mien_giam}`)
        .join("\n");

      return {
        id: x.id,
        ma_hs: x.ma_hs,
        ho_ten: x.ho_ten,
        ngay_sinh: x.ngay_sinh,
        trang_thai:
          x.dm_trangthaihocsinh_trang_thai ||
          x.trang_thai ||
          trangThaiMap.get(x.dm_trangthaihocsinh_id) ||
          "",
        ten_truong: x.ten_truong,
        ten_khoi: resolvedTenKhoi,
        ten_he: resolvedTenHe,
        ten_lop: x.ten_lop,
        summary,
        appliedPolicies,
        sf_hinhthucmiengiam_ids: selectedIds,
        has_discount: selectedIds.length > 0,
      };
    });

    // Ưu tiên hiển thị học sinh đã có miễn giảm lên đầu
    result.sort((a, b) => {
      if (a.has_discount !== b.has_discount) {
        return a.has_discount ? -1 : 1;
      }
      return (a.ho_ten || "").localeCompare(b.ho_ten || "");
    });

    return result;
  }, [
    ts_hocsinhs,
    sf_dexuatmiengiams,
    sf_hinhthucmiengiams,
    categorySource,
    dmTrangThaiHocSinhs,
    dm_coso_id,
    nhom_mien_giam_id,
    hinh_thuc_mien_giam_ids,
  ]);

  const counts = useMemo(() => {
    const total = allStudentsProcessed.length;
    const withDiscount = allStudentsProcessed.filter(
      (x) => x.has_discount
    ).length;
    return {
      total,
      withDiscount,
      noDiscount: total - withDiscount,
    };
  }, [allStudentsProcessed]);

  const dataSource = useMemo(() => {
    if (filterMode === "has_discount") {
      return allStudentsProcessed.filter((x) => x.has_discount);
    }
    if (filterMode === "no_discount") {
      return allStudentsProcessed.filter((x) => !x.has_discount);
    }
    return allStudentsProcessed;
  }, [allStudentsProcessed, filterMode]);

  const renderThaoTac = useCallback(
    (data: any) => {
      return (
        <Box sx={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
          <SelectBoxMultipleHinhThucMienGiam
            value={data.sf_hinhthucmiengiam_ids}
            onValueChanged={(value) => {
              handleGanMienGiam(data.id, nam_hoc, value);
            }}
            sf_hinhthucmiengiams={sf_hinhthucmiengiams}
          />
        </Box>
      );
    },
    [sf_hinhthucmiengiams, handleGanMienGiam, nam_hoc]
  );

  const columns = useMemo<IDataGridColumn[]>(() => {
    const result: IDataGridColumn[] = [
      {
        caption: "Trạng thái",
        id: "trang_thai",
        dataField: "trang_thai",
        width: 120,
        cellRender: (data: any) => {
          let color: any = "fg.default";
          switch (data.trang_thai) {
            case "New Enrollment":
              color = "accent.emphasis";
              break;
            case "Thôi học":
              color = "danger.emphasis";
              break;
            case "Đang học":
            case "Đang theo học":
              color = "success.fg";
              break;
            case "Học sinh lên lớp chưa đóng phí":
              color = "attention.emphasis";
              break;
            default:
              break;
          }
          return (
            <Box sx={{ color, fontWeight: 500, fontSize: 12 }}>
              <TextWithEllipse text={data.trang_thai || "—"} lineNumber={1} />
            </Box>
          );
        },
      },
      {
        id: "ma_hs",
        dataField: "ma_hs",
        caption: "Mã HS",
        width: 120,
        cellRender: (data: any) => (
          <Box sx={{ fontWeight: 600, color: "accent.fg", fontSize: 12 }}>
            {data.ma_hs}
          </Box>
        ),
      },
      {
        id: "ho_ten",
        dataField: "ho_ten",
        width: 210,
        caption: "Học sinh",
        isMainColumn: true,
        calculateCellValue: (data: any) =>
          [data.ho_ten, data.ma_hs].filter(Boolean).join(" "),
        cellRender: (data: any) => <HocSinhShortInfoNew data={data} />,
      },
      {
        dataField: "ten_lop",
        id: "ten_lop",
        caption: "Trường lớp",
        width: 190,
        calculateCellValue: (data: any) => {
          const tenHeText = data.ten_he
            ? /^hệ\s/i.test(data.ten_he.trim())
              ? data.ten_he.trim()
              : `Hệ ${data.ten_he.trim()}`
            : "";
          return [data.ten_lop, data.ten_khoi, tenHeText]
            .filter(Boolean)
            .join(", ");
        },
        cellRender: (data: any) => {
          const tenHeText = data.ten_he
            ? /^hệ\s/i.test(data.ten_he.trim())
              ? data.ten_he.trim()
              : `Hệ ${data.ten_he.trim()}`
            : "";
          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "baseline",
                lineHeight: 1.35,
              }}
            >
              <Box sx={{ fontWeight: 600 }}>{data.ten_lop || "—"}</Box>
              {(data.ten_khoi || tenHeText) && (
                <Box sx={{ fontSize: 12, color: "fg.muted" }}>
                  {data.ten_khoi || ""}
                  {data.ten_khoi && tenHeText ? " • " : ""}
                  {tenHeText}
                </Box>
              )}
            </Box>
          );
        },
      },
      {
        caption: `Miễn giảm đang áp dụng (${counts.withDiscount})`,
        id: "summary",
        dataField: "summary",
        width: 360,
        cellRender: (data: any) => {
          if (!data.summary) {
            return <span style={{ color: "#8c959f" }}>—</span>;
          }
          return (
            <Box sx={{ color: "fg.muted" }}>
              <TextWithEllipse text={`${data.summary ?? ""}`} lineNumber={1} />
            </Box>
          );
        },
      },
      {
        caption: "Gán miễn giảm",
        id: "thao_tac",
        dataField: "thao_tac",
        width: 300,
        cellRender: renderThaoTac,
      },
    ];

    return result;
  }, [renderThaoTac, counts.withDiscount]);

  return (
    <Box className={styles.container} sx={{ p: 2 }}>
      <Box className="col-md-12" sx={{ mt: 1 }}>
        <DataGrid
          title={
            <Text
              text="Danh sách miễn giảm học sinh"
              sx={{ fontSize: 16, fontWeight: 600 }}
            />
          }
          dataSource={dataSource}
          isLoading={status === PageBaseStatus.is_loading}
          height={window.innerHeight - 165}
          sorting={{ enable: true, mode: "asc" }}
          exportConfig={{ enable: true, fileName: "DanhSachMienGiamHocSinh" }}
          searchEnable={true}
          filterRow={{
            enable: true,
          }}
          actionComponent={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  border: "1px solid #d0d7de",
                  borderRadius: "6px",
                  overflow: "hidden",
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                }}
              >
                <PrimerButton
                  size="small"
                  variant={filterMode === "all" ? "primary" : "invisible"}
                  sx={{ borderRadius: 0, px: 2, whiteSpace: "nowrap" }}
                  onClick={() => setFilterMode("all")}
                >
                  Tất cả ({counts.total})
                </PrimerButton>
                <PrimerButton
                  size="small"
                  variant={
                    filterMode === "has_discount" ? "primary" : "invisible"
                  }
                  sx={{
                    borderRadius: 0,
                    px: 2,
                    borderLeft: "1px solid #d0d7de",
                    whiteSpace: "nowrap",
                  }}
                  onClick={() => setFilterMode("has_discount")}
                >
                  Có miễn giảm ({counts.withDiscount})
                </PrimerButton>
                <PrimerButton
                  size="small"
                  variant={
                    filterMode === "no_discount" ? "primary" : "invisible"
                  }
                  sx={{
                    borderRadius: 0,
                    px: 2,
                    borderLeft: "1px solid #d0d7de",
                    whiteSpace: "nowrap",
                  }}
                  onClick={() => setFilterMode("no_discount")}
                >
                  Chưa có ({counts.noDiscount})
                </PrimerButton>
              </Box>
              <Box>
                <ComboboxTruong
                  value={dm_truong_id}
                  onValueChanged={(value) => {
                    dispatch(changeTruong(value));
                  }}
                  isShowClearButton={true}
                  size="small"
                />
              </Box>
              <Box>
                <ComboboxKhoi
                  dm_truong_id={dm_truong_id}
                  value={dm_khoi_id}
                  width={150}
                  onValueChanged={(value) => {
                    dispatch(changeKhoi(value));
                  }}
                  isShowClearButton={true}
                  size="small"
                />
              </Box>
              <Box>
                <ComboboxHe
                  width={150}
                  dm_khoi_id={dm_khoi_id}
                  dm_truong_id={dm_truong_id}
                  value={dm_he_id}
                  onValueChanged={(value) => {
                    dispatch(changeHe(value));
                  }}
                  isShowClearButton={true}
                  size="small"
                />
              </Box>
              <Button
                variant="default"
                size="small"
                leadingVisual={SyncIcon}
                onClick={() => {
                  dispatch(
                    loadGanMienGiamHocSinhStart({
                      dm_he_id: dm_he_id || 0,
                      dm_khoi_id: dm_khoi_id || 0,
                      dm_truong_id: dm_truong_id || 0,
                      nam_hoc: nam_hoc,
                    })
                  );
                  dispatch(
                    loadGanMienGiamStart({
                      dm_he_id: dm_he_id || 0,
                      dm_khoi_id: dm_khoi_id || 0,
                      dm_truong_id: dm_truong_id || 0,
                      nam_hoc: nam_hoc,
                    })
                  );
                }}
              >
                Làm mới
              </Button>
              <Button
                variant="primary"
                size="small"
                leadingVisual={UploadIcon}
                onClick={() => {
                  dispatch(showGanMienGiamImportModal());
                }}
              >
                Import
              </Button>
            </Box>
          }
          columns={columns}
          paging={{
            enable: true,
            pageSize: 50,
            pageSizeItems: [50, 100, 200, 500],
          }}
        />
      </Box>

      {is_show_filter_student_modal && (
        <GanMienGiamFilterStudentModal
          animationOf="#btnFilter"
          filterStudent={filterStudent}
          onClose={() => {
            dispatch(closeGanMienGiamFilterStudentModal());
          }}
          onSubmit={(filter: ganMienGiamFilterStudentModel) => {
            dispatch(changeGanMienGiamFilterStudent(filter));
            dispatch(closeGanMienGiamFilterStudentModal());
          }}
        />
      )}
      {is_show_approve_confirm && approve_request && (
        <AnimationConfirm
          animationOf={`btnApprove${approve_request?.sf_hinhthucmiengiam_id}`}
          onClose={() => {
            dispatch(approveGanMienGiamCancel());
          }}
          onConfirmed={() => {
            dispatch(approveGanMienGiamSubmit(approve_request));
          }}
          text={`Bạn có chắc chắn muốn ${
            approve_request.is_approved ? "áp dụng" : "bỏ áp dụng"
          } cho tất cả học sinh đang hiển thị`}
          type={approve_request.is_approved ? "success" : "danger"}
          is_saving={status === PageBaseStatus.is_saving}
          icon="fas fa-tasks"
          text_confirm_button={
            approve_request.is_approved ? "Áp dụng" : "Bỏ áp dụng"
          }
        />
      )}
      {is_show_import_modal && (
        <GanMienGiamImportModal
          nam_hoc={nam_hoc}
          animationOf="#btnImport"
          onClose={() => {
            dispatch(closeGanMienGiamImportModal());
          }}
          onSuccess={() => {
            dispatch(closeGanMienGiamImportModal());
            dispatch(
              loadGanMienGiamStart({
                dm_he_id: dm_he_id || 0,
                dm_khoi_id: dm_khoi_id || 0,
                dm_truong_id: dm_truong_id || 0,
                nam_hoc: nam_hoc,
              })
            );
          }}
        />
      )}
    </Box>
  );
};
export default GanMienGiamNewPage;
